/**
 * Spotting the second half of a double-click on a sticky note.
 *
 * This is a module rather than a ref inside Sticky because the two clicks do
 * not land on the same element. The first click opens the task workspace, and
 * the workspace covers the board — so the second click arrives on the sheet's
 * scrim, not on the note underneath it. The alternative was to hold every
 * single click for a quarter of a second to see whether a second one was
 * coming, which would put a delay on the most common action in the app to pay
 * for the rarest one.
 *
 * So the note records that it was just opened, and whoever the second click
 * reaches asks whether it is a double.
 */

/** How long after the first click a second one still counts. */
const DOUBLE_MS = 400;
/** And how far it may land from the first, in screen pixels. */
const DOUBLE_SLOP = 12;

let recent: { taskId: string; at: number; x: number; y: number } | null = null;

/** Called when a click opens a task, with where that click landed. */
export function noteOpened(taskId: string, x: number, y: number): void {
  recent = { taskId, at: performance.now(), x, y };
}

/**
 * Is this click the second half of a double on `taskId`? Consumes the record
 * either way, so a third click starts a fresh count rather than firing again.
 */
export function isSecondClick(taskId: string, x: number, y: number): boolean {
  const first = recent;
  recent = null;
  return (
    first !== null &&
    first.taskId === taskId &&
    performance.now() - first.at < DOUBLE_MS &&
    Math.hypot(x - first.x, y - first.y) < DOUBLE_SLOP
  );
}

/** A drag is not half of a double-click, and neither is closing the sheet. */
export function forgetClick(): void {
  recent = null;
}
